import { useSizes } from '@hg/stylizator/hooks'
import { Text } from '@hg/stylizator/typography'
import { Flex } from '@hg/stylizator/utils'
import { sprintf } from '@wordpress/i18n'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import * as React from 'react'
import styled, { useTheme } from 'styled-components'

import { useTranslation } from 'app/hooks/use-translation'
import { Author } from 'app/service/api/theme/author'
import { useGlobalState } from 'app/store'
import 'dayjs/locale/en'
import 'dayjs/locale/pt'
import 'dayjs/locale/es'
import 'dayjs/locale/ja'
import 'dayjs/locale/ru'
import 'dayjs/locale/fr'
import 'dayjs/locale/hi'

dayjs.extend(localizedFormat)
dayjs.extend(localeData)

interface IProps {
  author?: Author
  createdAt: string
  updatedAt: string
  readingTime: number
  textSmall?: boolean
}

const PostMeta: React.FC<IProps> = ({ author, createdAt, updatedAt, readingTime, textSmall }) => {
  const theme = useTheme()
  const { isDesktop } = useSizes()
  const t = useTranslation()
  const [locale] = useGlobalState('locale')
  const authorFullName = [author?.name ?? '', author?.surname ?? ''].filter((it) => it !== '').join(' ') || 'Honeygain'
  const createdAtDate = dayjs(createdAt)
  const updatedAtDate = dayjs(updatedAt)

  const renderEditedAt = () => {
    if (!updatedAtDate.isAfter(createdAtDate, 'day')) {
      return null
    }

    return <>&#x2022; {sprintf(t('last_updated'), updatedAtDate.locale(locale).format('ll'))}</>
  }

  const dateTextType = React.useMemo(() => {
    if (textSmall) {
      return 'subtitle'
    }

    return isDesktop ? 'body' : 'body_small'
  }, [textSmall, isDesktop])

  return (
    <Flex align="center">
      {!!author && <Avatar src={author.avatar_url} width={32} height={32} />}
      <Flex flexDir="column" flex={1}>
        {!!author && (
          <Text type={isDesktop ? 'body' : 'body_small'} fontWeight={700}>
            <span>{sprintf(t('by'), authorFullName)}</span>
          </Text>
        )}
        <Text type={dateTextType} color={theme.color.gray[60]}>
          <span>
            {createdAtDate.locale(locale).format('ll')} {renderEditedAt()} &#x2022;{' '}
            {sprintf(t('min_read'), readingTime)}
          </span>
        </Text>
      </Flex>
    </Flex>
  )
}

export default PostMeta

const Avatar = styled.img`
  margin-right: 8px;
  border-radius: 9999px;
  overflow: hidden;
  mix-blend-mode: multiply;
  max-width: 32px;
`
